import React from "react";
import axios from "axios";
import { toast } from "sonner";
import SiteLayout from "@/components/SiteLayout";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowRight, Lock, LogOut } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "evara_admin_token";

const STATUSES = ["new", "contacted", "scheduled", "completed", "archived"];

function StatusBadge({ value }) {
  const color = {
    new: "bg-[var(--evara-bg-mint)] text-[var(--evara-primary)]",
    contacted: "bg-[#E8EEF1] text-[#3A5A6F]",
    scheduled: "bg-[#EEE9DC] text-[#7A6326]",
    completed: "bg-[#DDEDE0] text-[#2F4833]",
    archived: "bg-[#ECECEC] text-[#666]",
  }[value] || "bg-[var(--evara-bg-mint)]";
  return (
    <span className={`inline-block px-2.5 py-1 rounded-full text-[10px] uppercase tracking-[0.14em] font-semibold ${color}`}>
      {value}
    </span>
  );
}

function LoginScreen({ onLogin }) {
  const [token, setToken] = React.useState("");
  const [loading, setLoading] = React.useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { token });
      localStorage.setItem(TOKEN_KEY, data.token);
      onLogin(data.token);
      toast.success("Welcome back.");
    } catch {
      toast.error("Invalid admin token.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <SiteLayout>
      <section className="section-pad" data-testid="admin-login">
        <div className="max-w-md mx-auto card-evara p-10">
          <div className="flex items-center gap-3">
            <Lock size={18} className="text-[var(--evara-primary)]" />
            <span className="eyebrow">Admin access</span>
          </div>
          <h1 className="font-serif text-3xl mt-4 leading-tight">EVARA Admin</h1>
          <p className="mt-3 text-sm text-[var(--evara-text-2)]">Enter your admin token to view consultations and contact submissions.</p>
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <label className="evara-label">Admin token</label>
              <input
                type="password"
                className="evara-input"
                value={token}
                onChange={(e) => setToken(e.target.value)}
                placeholder="Enter token"
                data-testid="admin-token-input"
                required
              />
            </div>
            <button className="btn-primary w-full justify-center" type="submit" disabled={loading} data-testid="admin-login-btn">
              {loading ? "Signing in…" : "Sign in"} {!loading && <ArrowRight size={16} />}
            </button>
          </form>
        </div>
      </section>
    </SiteLayout>
  );
}

export default function Admin() {
  const [token, setToken] = React.useState(() => localStorage.getItem(TOKEN_KEY));
  const [stats, setStats] = React.useState(null);
  const [consultations, setConsultations] = React.useState([]);
  const [contacts, setContacts] = React.useState([]);
  const [loading, setLoading] = React.useState(false);

  const headers = React.useMemo(() => ({ "X-Admin-Token": token }), [token]);

  const fetchAll = React.useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [s, c, k] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers }),
        axios.get(`${API}/consultations`, { headers }),
        axios.get(`${API}/contacts`, { headers }),
      ]);
      setStats(s.data);
      setConsultations(c.data);
      setContacts(k.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
        toast.error("Session expired. Please sign in again.");
      } else {
        toast.error("Failed to load data.");
      }
    } finally {
      setLoading(false);
    }
  }, [token, headers]);

  React.useEffect(() => { fetchAll(); }, [fetchAll]);

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/consultations/${id}?status=${encodeURIComponent(status)}`, null, { headers });
      setConsultations((cs) => cs.map((c) => (c.id === id ? { ...c, status } : c)));
      toast.success("Status updated.");
    } catch {
      toast.error("Update failed.");
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  };

  if (!token) return <LoginScreen onLogin={setToken} />;

  return (
    <SiteLayout>
      <section className="section-pad" data-testid="admin-dashboard">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
            <div>
              <div className="eyebrow">Admin</div>
              <h1 className="font-serif text-3xl md:text-5xl mt-3 leading-tight">Inbox</h1>
              <p className="mt-2 text-sm text-[var(--evara-text-2)]">Consultation requests and contact submissions.</p>
            </div>
            <div className="flex items-center gap-3">
              <button className="btn-secondary" onClick={fetchAll} data-testid="admin-refresh-btn">Refresh</button>
              <button className="btn-secondary" onClick={logout} data-testid="admin-logout-btn">
                <LogOut size={14} /> Sign out
              </button>
            </div>
          </div>

          {stats && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-10" data-testid="admin-stats">
              <div className="card-evara p-6">
                <div className="eyebrow">Total Consultations</div>
                <div className="font-serif text-4xl mt-2 text-[var(--evara-primary)]">{stats.consultations}</div>
              </div>
              <div className="card-evara p-6">
                <div className="eyebrow">New (unhandled)</div>
                <div className="font-serif text-4xl mt-2 text-[var(--evara-primary)]">{stats.new_consultations}</div>
              </div>
              <div className="card-evara p-6">
                <div className="eyebrow">Contact Messages</div>
                <div className="font-serif text-4xl mt-2 text-[var(--evara-primary)]">{stats.contacts}</div>
              </div>
            </div>
          )}

          <Tabs defaultValue="consultations" className="w-full">
            <TabsList className="bg-[var(--evara-bg-mint)]" data-testid="admin-tabs">
              <TabsTrigger value="consultations" data-testid="tab-consultations">Consultations</TabsTrigger>
              <TabsTrigger value="contacts" data-testid="tab-contacts">Contacts</TabsTrigger>
            </TabsList>

            <TabsContent value="consultations" className="mt-6">
              {loading && <p className="text-sm text-[var(--evara-text-2)]">Loading…</p>}
              {!loading && consultations.length === 0 && (
                <div className="card-evara p-10 text-center text-[var(--evara-text-2)]">No consultation requests yet.</div>
              )}
              <div className="space-y-4">
                {consultations.map((c) => (
                  <div key={c.id} className="card-evara p-6" data-testid={`consultation-${c.id}`}>
                    <div className="grid md:grid-cols-12 gap-4">
                      <div className="md:col-span-4">
                        <div className="font-serif text-xl text-[var(--evara-primary)]">{c.full_name}</div>
                        <div className="text-xs text-[var(--evara-muted)] mt-1">{c.role || "—"} · {c.organization || "—"}</div>
                        <div className="text-sm mt-2"><a href={`mailto:${c.email}`} className="hairline-link">{c.email}</a></div>
                        <div className="text-sm text-[var(--evara-text-2)]">{c.phone}</div>
                      </div>
                      <div className="md:col-span-4">
                        <div className="eyebrow">Service</div>
                        <div className="text-sm mt-1">{c.service_interest}</div>
                        <div className="eyebrow mt-4">Preferred slot</div>
                        <div className="text-sm mt-1">{c.preferred_date} · {c.preferred_time}</div>
                      </div>
                      <div className="md:col-span-4">
                        <div className="flex items-center justify-between">
                          <StatusBadge value={c.status} />
                          <span className="text-xs text-[var(--evara-muted)]">{new Date(c.created_at).toLocaleString()}</span>
                        </div>
                        <select
                          className="evara-input mt-3"
                          value={c.status}
                          onChange={(e) => updateStatus(c.id, e.target.value)}
                          data-testid={`consultation-status-${c.id}`}
                        >
                          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </div>
                    </div>
                    {c.message && (
                      <>
                        <div className="divider-rule my-5" />
                        <p className="text-sm text-[var(--evara-text-2)] leading-relaxed">{c.message}</p>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="contacts" className="mt-6">
              {!loading && contacts.length === 0 && (
                <div className="card-evara p-10 text-center text-[var(--evara-text-2)]">No contact messages yet.</div>
              )}
              <div className="space-y-4">
                {contacts.map((c) => (
                  <div key={c.id} className="card-evara p-6" data-testid={`contact-${c.id}`}>
                    <div className="flex justify-between flex-wrap gap-2">
                      <div>
                        <div className="font-serif text-xl text-[var(--evara-primary)]">{c.name}</div>
                        <a href={`mailto:${c.email}`} className="hairline-link text-sm">{c.email}</a>
                      </div>
                      <span className="text-xs text-[var(--evara-muted)]">{new Date(c.created_at).toLocaleString()}</span>
                    </div>
                    {c.subject && <div className="eyebrow mt-4">{c.subject}</div>}
                    <p className="mt-3 text-sm text-[var(--evara-text-2)] leading-relaxed">{c.message}</p>
                  </div>
                ))}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>
    </SiteLayout>
  );
}
